// DemoLoader.ts — imports all demo data modules and exposes a unified registry
// with helpers for headline resolution, industry mapping, and pre-computed pre-reads.

import type { Segment } from '@/lib/types'
import type { EvidenceRow } from '@/lib/ensemble/types'

import {
  DEMO_IDS,
  type DemoId,
  DEMO_APPROVED_HEADLINES,
  INDUSTRY_TO_DEMO_ID,
  DEMO_AHA_CARDS,
  DEMO_CONFIGS,
  type DemoAhaCard,
} from '@/lib/demo/constants'

// Insurance-renewal data lives under src/lib/demo/data
import {
  DEMO_STIMULI as INSURANCE_STIMULI,
  DEMO_SEGMENTS as INSURANCE_SEGMENTS,
  DEMO_EVIDENCE as INSURANCE_EVIDENCE,
  DEMO_PRE_READ_QUESTION as INSURANCE_PRE_READ_QUESTION,
} from '@/lib/demo/data'

// Other demo data lives under src/data/demo/<slug>/data
import {
  DEMO_STIMULI as FIN_STIMULI,
  DEMO_SEGMENTS as FIN_SEGMENTS,
  DEMO_EVIDENCE as FIN_EVIDENCE,
  DEMO_PRE_READ_QUESTION as FIN_PRE_READ_QUESTION,
} from '@/data/demo/financial-services-fees/data'

import {
  DEMO_STIMULI as HC_STIMULI,
  DEMO_SEGMENTS as HC_SEGMENTS,
  DEMO_EVIDENCE as HC_EVIDENCE,
  DEMO_PRE_READ_QUESTION as HC_PRE_READ_QUESTION,
} from '@/data/demo/healthcare-reminder/data'

import {
  DEMO_STIMULI as SAAS_STIMULI,
  DEMO_SEGMENTS as SAAS_SEGMENTS,
  DEMO_EVIDENCE as SAAS_EVIDENCE,
  DEMO_PRE_READ_QUESTION as SAAS_PRE_READ_QUESTION,
} from '@/data/demo/saas-churn/data'

import {
  DEMO_STIMULI as PREF_STIMULI,
  DEMO_SEGMENTS as PREF_SEGMENTS,
  DEMO_EVIDENCE as PREF_EVIDENCE,
  DEMO_PRE_READ_QUESTION as PREF_PRE_READ_QUESTION,
} from '@/data/demo/preference-center/data'

// ---------------------------------------------------------------------------
// DemoData — canonical shape for a loaded demo
// ---------------------------------------------------------------------------

export interface DemoData {
  demoId: DemoId
  title: string
  preReadQuestion: string
  approvedHeadline: string
  segments: Segment[]
  stimuli: Array<{ stimulus_id: string; label: string; content: string; type: string }>
  evidence: EvidenceRow[]
  ahaCards: DemoAhaCard[]
}

// ---------------------------------------------------------------------------
// DEMO_REGISTRY
// ---------------------------------------------------------------------------

export const DEMO_REGISTRY: Record<DemoId, DemoData> = {
  'insurance-renewal': {
    demoId: 'insurance-renewal',
    title: DEMO_CONFIGS['insurance-renewal'].title,
    preReadQuestion: INSURANCE_PRE_READ_QUESTION,
    approvedHeadline: DEMO_APPROVED_HEADLINES['insurance-renewal'],
    segments: INSURANCE_SEGMENTS,
    stimuli: INSURANCE_STIMULI,
    evidence: INSURANCE_EVIDENCE,
    ahaCards: DEMO_AHA_CARDS['insurance-renewal'],
  },
  'financial-services-fees': {
    demoId: 'financial-services-fees',
    title: DEMO_CONFIGS['financial-services-fees'].title,
    preReadQuestion: FIN_PRE_READ_QUESTION,
    approvedHeadline: DEMO_APPROVED_HEADLINES['financial-services-fees'],
    segments: FIN_SEGMENTS,
    stimuli: FIN_STIMULI,
    evidence: FIN_EVIDENCE,
    ahaCards: DEMO_AHA_CARDS['financial-services-fees'],
  },
  'healthcare-reminder': {
    demoId: 'healthcare-reminder',
    title: DEMO_CONFIGS['healthcare-reminder'].title,
    preReadQuestion: HC_PRE_READ_QUESTION,
    approvedHeadline: DEMO_APPROVED_HEADLINES['healthcare-reminder'],
    segments: HC_SEGMENTS,
    stimuli: HC_STIMULI,
    evidence: HC_EVIDENCE,
    ahaCards: DEMO_AHA_CARDS['healthcare-reminder'],
  },
  'saas-churn': {
    demoId: 'saas-churn',
    title: DEMO_CONFIGS['saas-churn'].title,
    preReadQuestion: SAAS_PRE_READ_QUESTION,
    approvedHeadline: DEMO_APPROVED_HEADLINES['saas-churn'],
    segments: SAAS_SEGMENTS,
    stimuli: SAAS_STIMULI,
    evidence: SAAS_EVIDENCE,
    ahaCards: DEMO_AHA_CARDS['saas-churn'],
  },
  'preference-center-research': {
    demoId: 'preference-center-research',
    title: DEMO_CONFIGS['preference-center-research'].title,
    preReadQuestion: PREF_PRE_READ_QUESTION,
    approvedHeadline: DEMO_APPROVED_HEADLINES['preference-center-research'],
    segments: PREF_SEGMENTS,
    stimuli: PREF_STIMULI,
    evidence: PREF_EVIDENCE,
    ahaCards: DEMO_AHA_CARDS['preference-center-research'],
  },
}

// ---------------------------------------------------------------------------
// loadDemo
// ---------------------------------------------------------------------------

export function loadDemo(demoId: DemoId): DemoData {
  return DEMO_REGISTRY[demoId]
}

// ---------------------------------------------------------------------------
// pickDemoForIndustry
// ---------------------------------------------------------------------------

export function pickDemoForIndustry(industrySlug: string): DemoId {
  return INDUSTRY_TO_DEMO_ID[industrySlug] ?? 'saas-churn'
}

// ---------------------------------------------------------------------------
// jaccardSimilarity — word-set Jaccard between two strings
// ---------------------------------------------------------------------------

export function jaccardSimilarity(a: string, b: string): number {
  const tokenize = (s: string): Set<string> =>
    new Set(s.toLowerCase().split(/\W+/).filter(Boolean))

  const setA = tokenize(a)
  const setB = tokenize(b)

  const intersection = new Set([...setA].filter((w) => setB.has(w)))
  const union = new Set([...setA, ...setB])

  if (union.size === 0) return 1
  return intersection.size / union.size
}

// ---------------------------------------------------------------------------
// resolveHeadline
// ---------------------------------------------------------------------------

export function resolveHeadline(demoId: DemoId, generated?: string): string {
  const approved = DEMO_APPROVED_HEADLINES[demoId]
  if (generated === undefined) return approved
  return jaccardSimilarity(approved, generated) >= 0.7 ? generated : approved
}

// ---------------------------------------------------------------------------
// PreComputedPreRead — deterministic pre-read result shape
// ---------------------------------------------------------------------------

export interface PassSummary {
  passName: string
  summary: string
  score: number
}

export interface SegmentBreakdown {
  segmentName: string
  verdict: string
  score: number
}

export interface PreComputedPreRead {
  demoId: DemoId
  headline: string
  confidence: number
  passSummaries: PassSummary[]
  segmentBreakdown: SegmentBreakdown[]
  ahaCards: DemoAhaCard[]
  generatedAt: number
}

// ---------------------------------------------------------------------------
// getPreComputedPreRead
// ---------------------------------------------------------------------------

export function getPreComputedPreRead(demoId: DemoId): PreComputedPreRead {
  switch (demoId) {
    case 'insurance-renewal':
      return {
        demoId: 'insurance-renewal',
        headline: DEMO_APPROVED_HEADLINES['insurance-renewal'],
        confidence: 0.74,
        passSummaries: [
          { passName: 'Evidence-Grounded', summary: 'B', score: 0.81 },
          { passName: 'Skeptical', summary: 'B', score: 0.68 },
          { passName: 'Segment', summary: 'B', score: 0.72 },
          { passName: 'Contradiction', summary: 'B', score: 0.79 },
        ],
        segmentBreakdown: [
          { segmentName: 'General Homeowners', verdict: 'B', score: 0.82 },
          { segmentName: 'Recent Claimants', verdict: 'Uncertain', score: 0.38 },
          { segmentName: 'Price-Sensitive', verdict: 'B', score: 0.61 },
        ],
        ahaCards: DEMO_AHA_CARDS['insurance-renewal'],
        generatedAt: 1700000000000,
      }

    case 'financial-services-fees':
      return {
        demoId: 'financial-services-fees',
        headline: DEMO_APPROVED_HEADLINES['financial-services-fees'],
        confidence: 0.71,
        passSummaries: [
          { passName: 'Evidence-Grounded', summary: 'B', score: 0.78 },
          { passName: 'Compliance', summary: 'B', score: 0.88 },
          { passName: 'Segment', summary: 'B', score: 0.69 },
          { passName: 'Contradiction', summary: 'B', score: 0.74 },
        ],
        segmentBreakdown: [
          { segmentName: 'College-Educated', verdict: 'B', score: 0.76 },
          { segmentName: 'Non-College', verdict: 'B-strong', score: 0.84 },
          { segmentName: 'High-Balance', verdict: 'B', score: 0.71 },
        ],
        ahaCards: DEMO_AHA_CARDS['financial-services-fees'],
        generatedAt: 1700000000000,
      }

    case 'healthcare-reminder':
      return {
        demoId: 'healthcare-reminder',
        headline: DEMO_APPROVED_HEADLINES['healthcare-reminder'],
        confidence: 0.78,
        passSummaries: [
          { passName: 'Evidence-Grounded', summary: 'C', score: 0.82 },
          { passName: 'Literacy', summary: 'C', score: 0.91 },
          { passName: 'Segment', summary: 'C', score: 0.77 },
          { passName: 'Channel', summary: 'C-SMS', score: 0.69 },
        ],
        segmentBreakdown: [
          { segmentName: 'Standard-Literacy', verdict: 'C', score: 0.74 },
          { segmentName: 'Low-Literacy', verdict: 'C-strong', score: 0.89 },
          { segmentName: 'Elderly', verdict: 'B', score: 0.62 },
        ],
        ahaCards: DEMO_AHA_CARDS['healthcare-reminder'],
        generatedAt: 1700000000000,
      }

    case 'saas-churn':
      return {
        demoId: 'saas-churn',
        headline: DEMO_APPROVED_HEADLINES['saas-churn'],
        confidence: 0.82,
        passSummaries: [
          { passName: 'Attribution', summary: 'price-23pp', score: 0.86 },
          { passName: 'Behavioral', summary: 'activation-failure', score: 0.81 },
          { passName: 'Segment', summary: 'low-ARR-activation', score: 0.78 },
          { passName: 'Contradiction', summary: 'satisficing-29pct', score: 0.74 },
        ],
        segmentBreakdown: [
          { segmentName: 'Low-ARR', verdict: 'Activation-failure', score: 0.84 },
          { segmentName: 'Mid-ARR', verdict: 'Feature-gaps', score: 0.73 },
          { segmentName: 'High-ARR', verdict: 'Competitive', score: 0.71 },
        ],
        ahaCards: DEMO_AHA_CARDS['saas-churn'],
        generatedAt: 1700000000000,
      }

    case 'preference-center-research':
      return {
        demoId: 'preference-center-research',
        headline: DEMO_APPROVED_HEADLINES['preference-center-research'],
        confidence: 0.84,
        passSummaries: [
          { passName: 'Evidence-Grounded', summary: 'B-panel-wide', score: 0.84 },
          { passName: 'Segment', summary: 'A-high-engagement', score: 0.79 },
          { passName: 'Behavioral', summary: 'B-reduces-opt-outs', score: 0.81 },
          { passName: 'Contradiction', summary: 'C-stated-vs-predicted', score: 0.76 },
          { passName: 'Respondent Persona', summary: 'B-general', score: 0.83 },
        ],
        segmentBreakdown: [
          { segmentName: 'General customers', verdict: 'B', score: 0.84 },
          { segmentName: 'High-engagement / under-35', verdict: 'A', score: 0.79 },
          { segmentName: 'Recent opt-outs', verdict: 'B', score: 0.71 },
        ],
        ahaCards: DEMO_AHA_CARDS['preference-center-research'],
        generatedAt: 1700000000000,
      }
  }
}

// Re-export DemoId and DEMO_IDS for consumers who only import from DemoLoader
export type { DemoId }
export { DEMO_IDS }
